import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import PageShell from "../common/PageShell";
import { contentPageSx } from "../common/contentPageStyles";
import utils from "../../universal/utils";
import {
  DEFAULT_DISCORD_INVITE_URL,
  fetchPublicDiscordInviteUrl,
} from "../public-site/discordInviteUrl";

export default function PrivacyPolicyPage() {
  const theme = useTheme();
  const pageSx = React.useMemo(() => contentPageSx(theme), [theme]);
  const [discordInviteUrl, setDiscordInviteUrl] = React.useState(
    DEFAULT_DISCORD_INVITE_URL
  );

  React.useEffect(() => {
    let cancelled = false;

    fetchPublicDiscordInviteUrl(utils.getEdgeBasePath())
      .then((resolvedUrl) => {
        if (!cancelled) {
          setDiscordInviteUrl(resolvedUrl);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PageShell maxWidth="lg">
      <Paper sx={pageSx.root} elevation={4}>
          <Typography component="h1" variant="h4" sx={pageSx.headline}>
            Privacy Policy for Ouro
          </Typography>
          <Typography
            component="p"
            variant="body2"
            style={{ color: "#6e6e6e" }}
            sx={pageSx.smallSubTitle}
          >
            Last Updated: 18th of May, 2026
          </Typography>

          <Typography paragraph sx={pageSx.para}>
            At Ouro, your privacy is our priority. This Privacy Policy explains
            how we collect, use, and protect your personal information when you
            use our matchmaking service and interact with our website. By using
            Ouro, you agree to the practices described in this policy.
          </Typography>

          <Typography
            component="h2"
            variant="h5"
            style={{ color: "#6e6e6e" }}
            sx={pageSx.smallSubTitle}
          >
            1. Information We Collect
          </Typography>

          <Typography paragraph sx={pageSx.para}>
            We collect the following types of information to provide and improve
            our services:
            <ul>
              <li>
                Personal Information: When you register for an account, we may
                collect personal details such as your username, email address,
                and any other information you provide.
              </li>
              <li>
                Gameplay Data: We collect data related to your gaming activities
                on our platform, such as match history, in-game statistics, and
                behavioral patterns to enhance matchmaking fairness and
                accuracy.
              </li>
              <li>
                Technical Data: We collect technical information, such as your
                IP address, device type, browser type, and operating system, to
                ensure the security and functionality of our service.
              </li>
              <li>
                Location Inference: When you have not selected a country, we may
                use your IP address to infer your country for game server region
                suggestions. We store the derived country on your account as a
                non-authoritative fallback; your user-selected country takes
                priority.
              </li>
              <li>
                Communication Data: We may collect information from
                communications between you and Ouro, including customer support
                inquiries, feedback, and other correspondence.
              </li>
            </ul>
          </Typography>

          <Typography
            component="h2"
            variant="h6"
            style={{ color: "#6e6e6e" }}
            sx={pageSx.smallSubTitle}
          >
            2. How We Use Your Information
          </Typography>

          <Typography component={"p"} sx={pageSx.para}>
            <Typography sx={pageSx.para}>
              Ouro uses your information for the following purposes:
            </Typography>

            <Typography sx={pageSx.para}>
              <ul>
                <li>
                  Service Provision: To operate and maintain the matchmaking
                  service, including managing your account, facilitating
                  matches, and providing customer support.
                </li>
                <li>
                  Service Improvement: To analyze gameplay data and feedback,
                  enabling us to refine our algorithms, enhance user experience,
                  and create a fairer gaming environment.
                </li>
                <li>
                  Security: To detect and prevent fraudulent activities,
                  cheating, and other forms of abuse on our platform.
                </li>
                <li>
                  Communication: To send you important updates, notifications,
                  and information related to your account or our services.
                </li>
                <li>
                  Legal Compliance: To comply with legal obligations and respond
                  to lawful requests from authorities.
                </li>
              </ul>
            </Typography>
          </Typography>

          <Typography
            component="h2"
            variant="h6"
            style={{ color: "#6e6e6e" }}
            sx={pageSx.smallSubTitle}
          >
            3. How We Share Your Information
          </Typography>

          <Typography component={"p"} sx={pageSx.para}>
            We do not sell, trade, or rent your personal information to third
            parties. However, we may share your information with:
            <ul>
              <li>
                <strong>Service Providers:</strong> Trusted third-party
                companies that help us operate our service, such as hosting
                providers, data analysts, and customer support platforms. These
                providers are obligated to maintain the confidentiality of your
                information.
              </li>
              <li>
                <strong>Legal Authorities:</strong> We may disclose your
                information if required by law or to protect our rights,
                property, or the safety of our users.
              </li>
            </ul>
          </Typography>

          <Typography
            component="h2"
            variant="h6"
            style={{ color: "#6e6e6e" }}
            sx={pageSx.smallSubTitle}
          >
            4. Cookies and Tracking Technologies
          </Typography>

          <Typography component={"p"} sx={pageSx.para}>
            Ouro uses cookies and similar technologies to enhance your
            experience on our website. Cookies are small data files stored on
            your device that help us remember your preferences, understand how
            you interact with our service, and provide personalized content. You
            can manage your cookie preferences through your browser settings.
          </Typography>

          <Typography
            component="h2"
            variant="h6"
            style={{ color: "#6e6e6e" }}
            sx={pageSx.smallSubTitle}
          >
            5. Data Security
          </Typography>

          <Typography component={"p"} sx={pageSx.para}>
            We take the security of your data seriously and implement a variety
            of measures to protect it from unauthorized access, alteration,
            disclosure, or destruction. However, no method of transmission over
            the internet or electronic storage is completely secure, and we
            cannot guarantee absolute security.
          </Typography>

          <Typography
            component="h2"
            variant="h6"
            style={{ color: "#6e6e6e" }}
            sx={pageSx.smallSubTitle}
          >
            6. Your Rights
          </Typography>

          <Typography component={"p"} sx={pageSx.para}>
            <Typography component={"p"} sx={pageSx.para}>
              You have the following rights regarding your personal information:
            </Typography>
            <Typography component={"p"} sx={pageSx.para}>
              <ul>
                <li>
                  <strong>Access:</strong> You can request a copy of the
                  personal information we hold about you.
                </li>
                <li>
                  <strong>Correction:</strong> You can ask us to correct or
                  update inaccurate or incomplete information.
                </li>
                <li>
                  <strong>Deletion:</strong> You can request the deletion of
                  your account and associated data, subject to certain legal
                  obligations.
                </li>
                <li>
                  <strong>Opt-Out:</strong> You can opt-out of receiving
                  marketing communications from us by following the unsubscribe
                  instructions in those messages.
                </li>
              </ul>
              To exercise these rights, please contact us at support@ouro.is. If you are signed in, you can also submit an
              in-app deletion request from Account Settings in the Danger Zone.
            </Typography>
          </Typography>

          <Typography
            component="h2"
            variant="h6"
            style={{ color: "#6e6e6e" }}
            sx={pageSx.smallSubTitle}
          >
            7. Children’s Privacy
          </Typography>

          <Typography component={"p"} sx={pageSx.para}>
            Ouro is not intended for children under the age of 13, and we do not
            knowingly collect personal information from children under this age.
            If we become aware that we have inadvertently collected such
            information, we will take steps to delete it as soon as possible.
          </Typography>

          <Typography
            component="h2"
            variant="h6"
            style={{ color: "#6e6e6e" }}
            sx={pageSx.smallSubTitle}
          >
            8. Changes to This Privacy Policy
          </Typography>

          <Typography component={"p"} sx={pageSx.para}>
            <Typography component={"p"} sx={pageSx.para}>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices or legal requirements. We will notify you
              of any significant changes by posting the new policy on our
              website and updating the “Last Updated” date.
            </Typography>
          </Typography>

          <Typography
            component="h2"
            variant="h6"
            style={{ color: "#6e6e6e" }}
            sx={pageSx.smallSubTitle}
          >
            9. Contact Us
          </Typography>

          <Typography component={"p"} sx={pageSx.para}>
            If you have any questions or concerns about this Privacy Policy or
            our data practices, please contact us at:
          </Typography>

          <Typography
            component="h2"
            variant="h6"
            style={{ color: "#6e6e6e" }}
            sx={pageSx.smallSubTitle}
          >
            Ouro Support
          </Typography>

          <Box sx={pageSx.para}>
            <table>
              <tbody>
                <tr>
                  <td>
                    <img
                      style={{ height: 20 }}
                      src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/Gmail_Logo_64px.png"
                    />
                  </td>
                  <td>
                    <a
                      style={pageSx.regularLink}
                      href="mailto:support@ouro.is"
                    >
                      <Typography>support@ouro.is</Typography>
                    </a>
                  </td>
                </tr>
                <tr>
                  <td>
                    <img
                      style={{ height: 20 }}
                      src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/discord.png"
                    />
                  </td>
                  <td>
                    <a
                      style={pageSx.regularLink}
                      href={discordInviteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Typography>Discord</Typography>
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </Box>
      </Paper>
    </PageShell>
  );
}

PrivacyPolicyPage.propTypes = {
  actions: PropTypes.object,
  catalog: PropTypes.object,
};
